import { Badge } from 'features/catalog/useBadges';
import { compareBadgesByScoreAsc, rarityOrderIndex } from 'features/catalog/rarity';
import { sortBadgeHashByRarity } from 'features/catalog/rarity-config';

export const HIGHLIGHTS_SIZE = 5;
/** Search the rarest N claimed badges for a unique 5-set (C(25,5) is still cheap). */
const HIGHLIGHTS_SEARCH_POOL = 25;

const ownersOf = (badge: Badge): Set<string> => new Set(badge.participantIDsWhoOwnThisBadge);

const otherOwnersOfAll = (badges: Badge[], ownId: string): number => {
  let intersection: Set<string> | null = null;
  for (const badge of badges) {
    const owners = ownersOf(badge);
    if (intersection == null) {
      intersection = new Set(owners);
      continue;
    }
    intersection = new Set([...intersection].filter((id) => owners.has(id)));
  }
  if (!intersection) return 0;
  return [...intersection].filter((id) => id !== ownId).length;
};

/**
 * Badge Hash supersession: when you own a badge whose restrictedRules require
 * specific prerequisite badge(s) (upgrade / ladder), those prerequisites are
 * dropped from the hash so only the stronger badge shows.
 *
 * Only applies when every listed id is required (`count === ids.length`) —
 * e.g. Champion → Podium, Emerald Shield → Foundational Forest.
 * OR-style gates (`count: 1` of many offices/offsites) are left alone.
 */
const excludeSupersededBadges = (claimed: Badge[]): Badge[] => {
  const supersededIds = new Set<string>();
  for (const badge of claimed) {
    const rules = badge.restrictedRules;
    if (!rules?.ids.length || rules.count !== rules.ids.length) continue;
    for (const id of rules.ids) supersededIds.add(id);
  }
  return claimed.filter((badge) => !supersededIds.has(badge.id));
};

/** After the unique 5 are chosen by badgeScore, show them rarest-tier first. */
const compareHashDisplayOrder = (a: Badge, b: Badge): number =>
  rarityOrderIndex(a.rarityName) - rarityOrderIndex(b.rarityName) || compareBadgesByScoreAsc(a, b);

const finalizeHash = (badges: Badge[] | null): Badge[] | null => {
  if (!badges) return null;
  if (!sortBadgeHashByRarity) return badges;
  return [...badges].sort(compareHashDisplayOrder);
};

/** Apply the same optional rarity display sort used by Badge Hash. */
export const sortHashBadgesForDisplay = (badges: Badge[]): Badge[] => finalizeHash(badges) ?? badges;

/**
 * Pick 5 claimed badges nobody else owns as a set. Prefer lowest badgeScore first
 * (same ranking as the profile pic), then keep searching that pool until a unique combo exists.
 * The chosen 5 are then optionally sorted by rarity (`sortBadgeHashByRarity` in rarity-config).
 * Returns null when no unique combination exists → no Badge Hash.
 * Cached by profile + roster — ownership lists don't change after boot.
 */
const highlightCache = new Map<string, Badge[] | null>();

export const findUniqueHighlightBadges = (claimed: Badge[], ownId: string): Badge[] | null => {
  const candidates = excludeSupersededBadges(claimed);
  if (candidates.length < HIGHLIGHTS_SIZE) return null;

  const cacheKey = `${ownId}|${candidates
    .map((b) => b.id)
    .sort()
    .join(',')}`;
  if (highlightCache.has(cacheKey)) return highlightCache.get(cacheKey) ?? null;

  const sorted = [...candidates].sort(compareBadgesByScoreAsc);
  let result: Badge[] | null = null;
  if (otherOwnersOfAll(sorted.slice(0, HIGHLIGHTS_SIZE), ownId) === 0) {
    result = sorted.slice(0, HIGHLIGHTS_SIZE);
  } else {
    const pool = sorted.slice(0, Math.min(sorted.length, HIGHLIGHTS_SEARCH_POOL));
    const n = pool.length;
    outer: for (let i = 0; i < n - 4; i++) {
      for (let j = i + 1; j < n - 3; j++) {
        for (let k = j + 1; k < n - 2; k++) {
          for (let l = k + 1; l < n - 1; l++) {
            for (let m = l + 1; m < n; m++) {
              const combo = [pool[i], pool[j], pool[k], pool[l], pool[m]];
              if (otherOwnersOfAll(combo, ownId) === 0) {
                result = combo;
                break outer;
              }
            }
          }
        }
      }
    }
  }

  if (result == null && sorted.length > HIGHLIGHTS_SEARCH_POOL) {
    const selected: Badge[] = [sorted[0]];
    let coOwners = new Set([...ownersOf(sorted[0])].filter((id) => id !== ownId));
    const remaining = sorted.slice(1);

    while (selected.length < HIGHLIGHTS_SIZE && remaining.length > 0) {
      let bestIdx = 0;
      let bestRemaining = coOwners.size;
      for (let i = 0; i < remaining.length; i++) {
        const owners = ownersOf(remaining[i]);
        let next = 0;
        for (const id of coOwners) {
          if (owners.has(id)) next += 1;
        }
        if (next < bestRemaining) {
          bestRemaining = next;
          bestIdx = i;
        }
      }
      const picked = remaining.splice(bestIdx, 1)[0];
      selected.push(picked);
      const pickedOwners = ownersOf(picked);
      coOwners = new Set([...coOwners].filter((id) => pickedOwners.has(id)));
    }

    while (selected.length < HIGHLIGHTS_SIZE && remaining.length > 0) {
      selected.push(remaining.shift()!);
    }

    if (selected.length === HIGHLIGHTS_SIZE && otherOwnersOfAll(selected, ownId) === 0) {
      result = selected;
    }
  }

  result = finalizeHash(result);
  highlightCache.set(cacheKey, result);
  return result;
};
